import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getGallery, getInstitutions } from "services/content";
import PageHero from "components/shared/PageHero";
import GalleryCard from "components/shared/GalleryCard";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";
import { mediaUrl } from "lib/media";

export default function GalleryPage() {
  const { data: photos } = useResource(getGallery);
  const { data: institutions } = useResource(getInstitutions);
  const { inst } = useParams();
  const [active, setActive] = useState("All");
  const [sel, setSel] = useState(null);
  const closeRef = useRef(null);

  const meta = (institutions?.items || []).find((i) => i.slug === inst);

  // Scoping follows the notices rule: a photo with no tags isn't scoped to any
  // institution, so it shows everywhere. Tagged photos appear only under the
  // institutions they name.
  const all = (photos || []).filter(
    (p) => !inst || !p.tags?.length || p.tags.includes(inst)
  );
  // Filter chips are built from the categories that actually exist (incl. custom ones).
  const CATS = ["All", ...Array.from(new Set(all.map((p) => p.cat).filter(Boolean)))];
  const filtered =
    active === "All" ? all : all.filter((p) => p.cat === active);

  useEffect(() => {
    setActive("All");
  }, [inst]);

  // Lightbox: close on Escape, lock scroll, focus the close button.
  useEffect(() => {
    if (!sel) return;
    const onKey = (e) => e.key === "Escape" && setSel(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [sel]);

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="Photo Gallery"
        sub={
          meta
            ? `Campus life at ${meta.name}.`
            : "Celebrating academic excellence, cultural richness, and campus life at KCGS & KCCWC."
        }
        page={meta ? `Gallery › ${meta.short || meta.name}` : "Gallery"}
        bg={meta?.color ? `linear-gradient(140deg,#003d2b,${meta.color})` : undefined}
      />
      <section className="max-w-container mx-auto px-4 sm:px-10 py-14">
        {meta && (
          <div className="mb-8 pb-5 border-b border-line">
            <h2
              className="font-serif text-[26px] sm:text-[32px] font-bold leading-tight"
              style={{ color: meta.color || "#005C40" }}
            >
              {meta.name}
            </h2>
            {meta.kicker && (
              <div className="text-[13px] text-muted mt-1">{meta.kicker}</div>
            )}
          </div>
        )}

        {photos && all.length === 0 && (
          <p className="text-muted text-[15px]">
            No photos published for this institution yet.
          </p>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-9 flex-wrap">
          {CATS.map((c) => {
            const on = active === c;
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                aria-pressed={on}
                className="px-5 py-2 rounded-full border-[1.5px] font-sans font-semibold text-[13px] cursor-pointer transition-all duration-200 hover:border-green"
                style={{
                  borderColor: on ? "#005C40" : "#e2ddd6",
                  background: on ? "#005C40" : "#ffffff",
                  color: on ? "#fff" : "#5c5c5c",
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
          {!photos
            ? Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} className="rounded-xl" style={{ height: 220 }} />
              ))
            : filtered.map((p, i) => (
                <Reveal key={`${active}-${p.id}`} delay={(i % 3) * 80}>
                  <GalleryCard
                    item={{ ...p, span: 1 }}
                    height={220}
                    onClick={() => setSel(p)}
                  />
                </Reveal>
              ))}
        </div>
      </section>

      {/* Lightbox */}
      {sel && (
        <div
          onClick={() => setSel(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${sel.label} — ${sel.cat}`}
          className="fixed inset-0 bg-black/[0.87] z-[2000] flex items-center justify-center backdrop-blur-[8px] px-4 animate-fadeIn"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-[20px] w-full max-w-[640px] h-[400px] flex flex-col items-center justify-end relative overflow-hidden"
            style={{ background: sel.color }}
          >
            {mediaUrl(sel.image) ? (
              <img
                src={mediaUrl(sel.image)}
                alt={sel.label}
                className="absolute inset-0 w-full h-full object-contain bg-black/60"
              />
            ) : (
              <div className="text-6xl opacity-40 m-auto" aria-hidden="true">
                📷
              </div>
            )}
            <div className="relative z-10 w-full px-6 pt-14 pb-5 bg-gradient-to-t from-black/90 via-black/55 to-transparent">
              <p className="font-serif text-[22px] font-bold text-white text-center leading-snug drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                {sel.label}
              </p>
              <p className="text-white/75 text-[13px] text-center mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
                {sel.cat} · KCGS &amp; KCCWC
              </p>
            </div>
            <button
              ref={closeRef}
              onClick={() => setSel(null)}
              aria-label="Close image"
              className="absolute top-4 right-4 bg-white/[0.18] border-none text-white w-9 h-9 rounded-full text-lg cursor-pointer transition-colors hover:bg-white/30"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
